/* eslint-disable max-len */
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import JsonQuery from 'json-query';
import {
    hooks as commonHooks,
    types as commonTypes,
    utils as commonUtils,
} from '@sitebuilder/common';
import PieChart from '../PieChart/PieChart';
import HeaderWrapper from '../Header/HeaderWrapper';
import { Headings } from '../Header/Header';
import { headingLevels } from '../Header/Title';
import {
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import holdingsContainerStyles from './HoldingsContainer.styles';
import { sampleData } from './sampleData';

interface PieChartOptions {
    path?: string;
    value?: string;
    valueType?: string;
    functionToTransform?: string;
}
interface Holdings {
    name: string;
    title: string;
    subTitle?: string;
    header: string;
    headingLevel: headingLevels;
    subTitleLevel: headingLevels;
    value?: any;
    showChartTableOnly: boolean;
    pieTableHeaderRow: string;
    altText?: string;
}
interface TableColumn {
    label?: string;
    value?: string;
    isOnlyText?: boolean;
}
interface HoldingsContainerProps {
    containerTitles: Headings[];
    dataKey: string;
    holdings: Holdings[];
    options: PieChartOptions[];
    pieTableHeaderRow?: string;
    columns: TableColumn[];
    functionToTransform: string;
    transformData?: boolean;
    paletteId?: string;
    typographyClass?: string;
    disableTabStaticData?: boolean;
}

interface WidgetProperties extends commonTypes.WidgetPropertiesBase, HoldingsContainerProps {}

const HoldingsContainer = (props: commonTypes.WidgetInstanceProps) => {
    const widgetProperties = commonHooks.useWidgetProperties<WidgetProperties>(
        props.widgetInstanceId
    );
    const { disableTabStaticData: globalDisableTabStaticData } =
        commonHooks.useWidgetProperties<WidgetProperties>('root') || {};
    const {
        styles,
        containerTitles,
        dataKey,
        holdings,
        options,
        columns,
        functionToTransform,
        transformData,
        paletteId,
        disableTabStaticData,
    } = widgetProperties || {};
    const { data } = props;

    const i18n = commonHooks.useLocalization();

    const pieChartData: any = dataKey ? JsonQuery(`${dataKey}`, { data }).value : sampleData;

    let transformedData = pieChartData;

    const transformFunctionName = `${props.widgetInstanceId}-transform`;

    try {
        // eslint-disable-next-line no-new-func
        transformedData = transformData
            ? commonUtils.transformDataWithGlobals(transformFunctionName, functionToTransform, {
                  pieChartData,
                  _data: data,
                  handlebar: i18n.getHandlebars(),
              })
            : pieChartData;
    } catch (e) {
        console.log(e);
    }

    const _holdings = holdings || [
        { name: 'top10Holdings' },
        { name: 'geographicAllocations' },
        { name: 'sectorAllocations' },
    ];

    const holdingsData: any =
        Array.isArray(_holdings) &&
        _holdings.reduce(
            (acc: any, h) => {
                const { value } = JsonQuery(`[dimension=${h.name}].allocations`, {
                    data: transformedData || sampleData,
                });
                const title = JsonQuery(`[dimension=${h.name}].title`, {
                    data: transformedData,
                }).value;

                const subTitle = JsonQuery(`[dimension=${h.name}].subTitle`, {
                    data: transformedData,
                }).value;

                const header = JsonQuery(`[dimension=${h.name}].header`, {
                    data: transformedData,
                }).value;

                if (value) {
                    acc.render = true;
                    acc.holdings.push({
                        ...h,
                        value,
                        name: h?.name,
                        title: title || i18n.format(h.title || '', data) || h.title,
                        subTitle:
                            subTitle ||
                            i18n.format(h.subTitle || '', { ...data, transformedData }) ||
                            h.subTitle,
                        showChartTableOnly: h.showChartTableOnly,
                        pieTableHeaderRow:
                            header ||
                            i18n.format(h.pieTableHeaderRow || '', { ...data, transformedData }),
                        altText:
                            i18n.format(h?.altText || '', { ...data, transformedData }) ||
                            h?.altText,
                        headingLevel: h.headingLevel,
                    });
                }

                return acc;
            },
            { render: false, holdings: [] }
        );

    return (
        <>
            {holdingsData?.render && (
                <div
                    className={`holdings-container ${
                        widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
                    }`}
                    css={css`
                        ${holdingsContainerStyles}${getTextSchemaProperties(
                            widgetProperties,
                            data?.colorSpace
                        )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                            widgetProperties
                        )}${styles || ''}
                    `}
                >
                    <HeaderWrapper
                        headings={containerTitles}
                        data={data}
                        disableTabStaticData={globalDisableTabStaticData || disableTabStaticData}
                    />
                    <div
                        className={`pie-charts-container ${
                            data?.locale === 'fr-CA' ? 'french' : ''
                        }`}
                    >
                        {holdingsData.holdings?.map((h: Holdings, i: number) => {
                            const formattedColumns =
                                columns &&
                                columns.map((column) => ({
                                    ...column,
                                    label: i18n.format(column.label || '', data),
                                }));

                            return h.value.length ? (
                                <PieChart
                                    key={i}
                                    data={{
                                        holdings: h.value,
                                        themeSelected: data?.themeSelected,
                                        colorSpace: data?.colorSpace,
                                    }}
                                    dataKey={'holdings'}
                                    chartTitles={[
                                        { headingLevel: h.headingLevel, headerText: h.title },
                                        { headingLevel: h.subTitleLevel, headerText: h.subTitle },
                                    ]}
                                    options={options}
                                    columns={formattedColumns}
                                    showChartTableOnly={h.showChartTableOnly}
                                    pieTableHeaderRow={h.pieTableHeaderRow}
                                    altText={h?.altText}
                                    // eslint-disable-next-line
                                    colorPallete={(data as any)?.colorPallete}
                                    paletteId={paletteId}
                                    disableTabStaticData={
                                        globalDisableTabStaticData || disableTabStaticData
                                    }
                                />
                            ) : null;
                        })}
                    </div>
                </div>
            )}
        </>
    );
};

export default HoldingsContainer;
